import { useState } from "react";

import { Badge, type BadgeVariant } from "~/components/Badge";
import { Button } from "~/components/Button";
import { ReviewCard } from "~/components/ReviewCard";
import { Table } from "~/components/Table";
import { VerdictCard } from "~/components/VerdictCard";
import {
  REVIEW_GATE_TASK_TYPES,
  SET_FILEVINE_PROJECT_ID_TASK_TYPE,
  type Task,
} from "~/lib/tasks";
import type { EventState } from "~/lib/events";
import { formatDate } from "~/lib/dates";

import * as css from "~/components/TaskTable.css";
import * as proposalCss from "~/components/VerdictCard.css";

export interface TaskTableProps {
  tasks: Task[];
  /** Mark done / reject / reopen a task — the parent owns the event POST (and
   *  any subtype-specific companions / optimistic cache). */
  onIssue: (task: Task, state: EventState) => void;
  /** The task name currently mid-flight + which transition, so its row labels
   *  its own pending state. */
  pendingTaskName?: string | undefined;
  pendingState?: EventState | undefined;
  /** Resolve an exception gate: assign the Filevine case + complete it. When
   *  provided, the `set filevine project id` gate renders an FV-id input +
   *  Assign button (inside its ReviewCard) instead of Approve/Reject. Absent
   *  for cases (no such gate). */
  onResolveGate?: (task: Task, filevineProjectID: number) => void;
  resolvePending?: boolean;
}

/**
 * The project task table — the list of a project's tasks with their state,
 * actions, and (for review gates) the ReviewCard they gate on rendered beneath.
 * Shared by the case detail page and the document-uploads detail page since both
 * are projects (TMF-209).
 */
export function TaskTable(props: TaskTableProps) {
  return (
    <Table>
      <thead>
        <tr>
          <th>Task</th>
          <th>Role</th>
          <th>State</th>
          <th className={css.actionsCell} aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {props.tasks.map((task) => (
          <TaskRow key={task.id} task={task} {...props} />
        ))}
      </tbody>
    </Table>
  );
}

function TaskRow({
  task,
  onIssue,
  pendingTaskName,
  pendingState,
  onResolveGate,
  resolvePending,
}: { task: Task } & TaskTableProps) {
  const isManual = task.is_manual;
  const isReview = REVIEW_GATE_TASK_TYPES.has(task.type);
  const isTerminal = task.state === "complete" || task.state === "rejected";
  const inFlight = pendingTaskName === task.name;
  const inFlightState = inFlight ? pendingState : undefined;

  const isExceptionGate =
    onResolveGate !== undefined && task.type === SET_FILEVINE_PROJECT_ID_TASK_TYPE;

  // A review gate and the exception gate carry the Verdict they gate on and
  // render a ReviewCard (document + claim + fields + actions) in an expansion
  // row. The card owns their action controls, so the row's action cell stays
  // empty for an actionable gate.
  const isGateType = isReview || isExceptionGate;
  const verdict = task._embedded?.review?._embedded?.verdict;
  // Actionable = the human can still act. When terminal/pending, the row shows
  // Reopen / Processing and the card is read-only.
  const actionable = task.state !== "pending" && !isTerminal;

  // A gate row's review card collapses into the whole gray box: an actionable
  // gate opens expanded (the reviewer needs the document to decide); a
  // terminal one opens collapsed, so it reads like any other resolved task row
  // until the chevron is clicked.
  const hasCard = isGateType && verdict !== undefined;
  const [expanded, setExpanded] = useState(actionable);

  return (
    <>
      <tr>
        <td className={css.taskName}>
          {hasCard ? (
            <button
              type="button"
              className={`${css.chevron} ${expanded ? "" : css.chevronCollapsed}`}
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? "Collapse review" : "Expand review"}
            >
              &#x25BC;
            </button>
          ) : null}
          {task.type}
          <div className={css.taskDates}>
            Created {formatDate(task.created_at)}
            {task.completed_at ? ` · Completed ${formatDate(task.completed_at)}` : ""}
          </div>
        </td>
        <td className={css.taskRole}>{task.role || "—"}</td>
        <td>
          <Badge variant={taskStateVariant(task.state)}>{task.state || "todo"}</Badge>
        </td>
        <td className={css.actionsCell}>
          {task.state === "pending" ? (
            <Button type="button" variant="secondary" disabled>
              Processing…
            </Button>
          ) : isTerminal ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => onIssue(task, "todo")}
              disabled={inFlight}
            >
              {inFlight ? "Reopening…" : "Reopen"}
            </Button>
          ) : isGateType && verdict ? null : isManual ? ( // gate actions live in its ReviewCard
            <span className={css.actionGroup}>
              <Button
                type="button"
                variant="primary"
                onClick={() => onIssue(task, "complete")}
                disabled={inFlight}
              >
                {inFlightState === "complete" ? "Marking…" : "Mark done"}
              </Button>
              {isReview ? (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => onIssue(task, "rejected")}
                  disabled={inFlight}
                >
                  {inFlightState === "rejected" ? "Rejecting…" : "Reject"}
                </Button>
              ) : null}
            </span>
          ) : null}
        </td>
      </tr>
      {hasCard && expanded ? (
        <tr>
          <td colSpan={4} className={css.proposalCell}>
            <ReviewCard
              task={task}
              verdict={verdict}
              isException={isExceptionGate}
              actionable={actionable}
              onApprove={() => onIssue(task, "complete")}
              onReject={() => onIssue(task, "rejected")}
              onResolve={(filevineProjectID) => onResolveGate?.(task, filevineProjectID)}
              inFlight={inFlight}
              inFlightState={inFlightState}
              resolvePending={resolvePending ?? false}
            />
          </td>
        </tr>
      ) : !isGateType && verdict ? (
        <tr>
          <td colSpan={4} className={css.proposalCell}>
            <div className={proposalCss.stack}>
              <VerdictCard verdict={verdict} />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function taskStateVariant(state: string): BadgeVariant {
  if (state === "complete") return "success";
  if (state === "in progress") return "info";
  if (state === "pending") return "info";
  if (state === "cancelled" || state === "rejected") return "neutral";
  if (state === "delayed") return "warning";
  return "warning"; // todo / empty default
}
